import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  Flex,
  Text,
  Badge,
  Spacer,
  Spinner,
} from "@chakra-ui/react";
import rulesService from "../../services/rules.service";
import RuleSnapshotModal from "./RuleSnapshotModal";

const LIMIT = 10;

const summarize = (changes = {}) => {
  const parts = [];
  const changed = Object.keys(changes.changed || {});
  const added = Object.keys(changes.added || {});
  const removed = Object.keys(changes.removed || {});
  if (changed.length) parts.push(`Changed: ${changed.join(", ")}`);
  if (added.length) parts.push(`Added: ${added.join(", ")}`);
  if (removed.length) parts.push(`Removed: ${removed.join(", ")}`);
  return parts.length ? parts.join("  •  ") : "No field-level changes";
};

// Paginated audit timeline of rule changes for one country.
function RuleAuditModal({ isOpen, onClose, country, label }) {
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [snapshotHash, setSnapshotHash] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);

  // reset to the first page whenever the modal opens or the country changes
  useEffect(() => {
    if (isOpen) setPage(1);
  }, [isOpen, country]);

  useEffect(() => {
    if (!isOpen || !country) return;
    setLoading(true);
    setError("");
    rulesService.getCountryAudit(country, { page, limit: LIMIT }).then((res) => {
      setLoading(false);
      if (res && res.results) {
        setAudit(res.results);
        setTotalPages(res.totalPages || 1);
        setTotalResults(res.totalResults || 0);
      } else {
        setError((res && (res.warning || res.error)) || "Could not load history");
      }
    });
  }, [isOpen, country, page]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Change history — {label || country}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {loading && (
              <Box textAlign="center" py={6}>
                <Spinner />
              </Box>
            )}
            {error && (
              <Text color="red.500" fontSize="sm">
                {error}
              </Text>
            )}
            {!loading && !error && audit.length === 0 && (
              <Text fontSize="sm" color="gray.500">
                No changes recorded yet.
              </Text>
            )}
            {!loading &&
              audit.map((e) => (
                <Box key={e.id || e._id} borderWidth="1px" borderRadius="md" p={3} mb={2}>
                  <Flex alignItems="center" mb={1}>
                    <Badge colorScheme={e.action === "reset" ? "orange" : "green"}>{e.action}</Badge>
                    <Text fontSize="xs" ml={2} color="gray.600">
                      {new Date(e.createdAt).toLocaleString()}
                    </Text>
                    <Spacer />
                    <Text fontSize="xs" color="gray.600">
                      {e.changedBy}
                    </Text>
                  </Flex>
                  <Text fontSize="sm">{summarize(e.changes)}</Text>
                  {e.ruleSnapshotHash && (
                    <Button
                      mt={2}
                      size="xs"
                      variant="link"
                      colorScheme="teal"
                      onClick={() => setSnapshotHash(e.ruleSnapshotHash)}
                    >
                      View full rules at this point
                    </Button>
                  )}
                </Box>
              ))}
          </ModalBody>
          <ModalFooter>
            <Text fontSize="xs" color="gray.600">
              {totalResults > 0 ? `Page ${page} of ${totalPages} · ${totalResults} change(s)` : ""}
            </Text>
            <Spacer />
            <Button size="xs" mr={2} onClick={() => setPage((p) => Math.max(1, p - 1))} isDisabled={page <= 1 || loading}>
              Prev
            </Button>
            <Button
              size="xs"
              mr={3}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              isDisabled={page >= totalPages || loading}
            >
              Next
            </Button>
            <Button size="sm" onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <RuleSnapshotModal
        isOpen={!!snapshotHash}
        hash={snapshotHash}
        onClose={() => setSnapshotHash(null)}
        title={`Rules after change — ${label || country}`}
      />
    </>
  );
}

export default RuleAuditModal;
